<template>
  <div>
    <div
      role="status"
      class="sr-only"
      aria-live="polite"
    >
      {{ screenReaderMessage }}
    </div>
     <!-- Mobile layout -->
    <div v-if="isMdScreen">
      <EstimatedCosts :content="data.estimatedCosts"/>
      <component :is="topRightSection.component" :content="topRightSection.content"/>
      <component :is="bottomLeftSection.component" :content="bottomLeftSection.content"/>
      <AppointmentForm :content="data.appointmentForm"/>
    </div>

    <!-- Desktop layout -->
    <div class="desktop-layout hidden md:grid" v-else>
      <div>
        <EstimatedCosts :content="data.estimatedCosts"/>
        <hr class="text-gray-divider mx-16"/>
        <component :is="bottomLeftSection.component" :content="bottomLeftSection.content"/>
      </div>
      <div>
        <component :is="topRightSection.component" :content="topRightSection.content"/>
        <AppointmentForm :content="data.appointmentForm"/>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useGlobal } from '@/store'
import { defineComponent, computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'

import {
  KLoading,
  EstimatedCosts,
  MemberDiscounts,
  PriceComparison,
  DiscountsSection,
  OffersSection,
  AppointmentForm
} from '@/components'
import useScreen from '@/hooks/useScreen'
import { trackEvent } from '@/utils/analytics'
import { trackSelectionEventCJA } from '@/utils/adobe-analytics-data-layer'

export default defineComponent({
  name: 'Final',
  components: {
    KLoading,
    EstimatedCosts,
    MemberDiscounts,
    PriceComparison,
    DiscountsSection,
    OffersSection,
    AppointmentForm
  },
  setup () {
    const global = useGlobal()
    const { finalPageData: data, showEye360, packageId } = storeToRefs(global)
    const { isMdScreen } = useScreen()

    const screenReaderMessage = ref('')

    const isContentLoaded = computed(() => {
      return !!data.value?.estimatedCosts && Object.keys(data.value.estimatedCosts).length > 0
    })

    onMounted(() => {
      const checkForData = setInterval(() => {
        if (isContentLoaded.value && !screenReaderMessage.value) {
          screenReaderMessage.value = 'Estimated out of pocket costs have been loaded.'
          clearInterval(checkForData)
        }
      }, 100)

      return () => clearInterval(checkForData)
    })


    const topRightSection = computed(() => (
      showEye360.value
        ? { component: PriceComparison, content: data.value.priceComparison }
        : { component: MemberDiscounts, content: data.value.memberDiscounts }
    ))

    const bottomLeftSection = computed(() => (
      packageId.value
        ? { component: OffersSection, content: data.value.offersSection }
        : { component: DiscountsSection, content: data.value.discountsSection }
    ))

    trackEvent('event-price-display')
    trackSelectionEventCJA('event-price-display')

    return {
      data,
      topRightSection,
      bottomLeftSection,
      isMdScreen,
      screenReaderMessage
    }
  }
})
</script>

<style scoped>

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.desktop-layout {
  grid-template-columns: 62% 38%;
}
</style>
